/**
 * Utility functions for civil claims case management
 */

/**
 * Generate a unique case ID with CA prefix, current year, and random alphanumeric
 * @returns {string} A formatted case ID
 */
export const generateCaseId = () => {
  const prefix = 'CA';
  const year = new Date().getFullYear();
  const randomId = Math.random().toString(36).substring(2, 10).toUpperCase();
  
  return `${prefix}-${year}-${randomId}`;
};

/**
 * Create a new status history entry
 * @param {string} status - The status value
 * @param {string} notes - Notes about this status change
 * @returns {Object} A status history entry
 */
export const createStatusHistoryEntry = (status, notes = '') => {
  return {
    status,
    date: new Date().toISOString().split('T')[0],
    notes
  };
};

/**
 * Validate that a case has required fields for submission
 * @param {Object} caseData - The complete case data object
 * @returns {Object} Validation result with isValid and errors
 */
export const validateCaseForSubmission = (caseData) => {
  const errors = {};
  
  // Check case overview
  if (!caseData.caseOverview.caseId) {
    errors.caseId = 'Case ID is required';
  }
  
  // Check plaintiff information
  if (!caseData.partyInformation.plaintiff.name) {
    errors.plaintiffName = 'Plaintiff name is required';
  }
  
  if (!caseData.partyInformation.plaintiff.address.street || 
      !caseData.partyInformation.plaintiff.address.city ||
      !caseData.partyInformation.plaintiff.address.zipCode) {
    errors.plaintiffAddress = 'Complete plaintiff address is required';
  }
  
  // Check defendant information
  if (!caseData.partyInformation.defendant.name) {
    errors.defendantName = 'Defendant name is required';
  }
  
  // Check claim details
  if (!caseData.claimDetails.amount) {
    errors.claimAmount = 'Claim amount is required';
  }
  
  if (!caseData.claimDetails.description) {
    errors.claimDescription = 'Claim description is required';
  }
  
  // Check venue
  if (!caseData.venue.county) {
    errors.venueCounty = 'County is required';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * Format currency amount for display
 * @param {number|string} amount - The amount to format
 * @returns {string} Formatted currency string
 */
export const formatCurrency = (amount) => {
  if (!amount) return '';
  
  const numAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
  
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2
  }).format(numAmount);
};

/**
 * Convert a case object to a downloadable JSON file
 * @param {Object} caseData - The complete case data
 * @returns {string} Data URI for the JSON file
 */
export const caseDataToJsonUri = (caseData) => {
  const dataStr = JSON.stringify(caseData, null, 2);
  return 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
};

/**
 * Get list of California counties for venue selection
 * @returns {Array} List of California counties
 */
export const getCaliforniaCounties = () => {
  return [
    'Alameda', 'Alpine', 'Amador', 'Butte', 'Calaveras', 'Colusa',
    'Contra Costa', 'Del Norte', 'El Dorado', 'Fresno', 'Glenn', 'Humboldt',
    'Imperial', 'Inyo', 'Kern', 'Kings', 'Lake', 'Lassen', 'Los Angeles',
    'Madera', 'Marin', 'Mariposa', 'Mendocino', 'Merced', 'Modoc', 'Mono',
    'Monterey', 'Napa', 'Nevada', 'Orange', 'Placer', 'Plumas', 'Riverside',
    'Sacramento', 'San Benito', 'San Bernardino', 'San Diego', 'San Francisco',
    'San Joaquin', 'San Luis Obispo', 'San Mateo', 'Santa Barbara', 'Santa Clara',
    'Santa Cruz', 'Shasta', 'Sierra', 'Siskiyou', 'Solano', 'Sonoma', 'Stanislaus',
    'Sutter', 'Tehama', 'Trinity', 'Tulare', 'Tuolumne', 'Ventura', 'Yolo', 'Yuba'
  ];
};

/**
 * Get civil claims filing fee based on claim amount (California rates)
 * @param {number|string} claimAmount - The claim amount
 * @returns {number} Filing fee amount
 */
export const getFilingFee = (claimAmount) => {
  const amount = typeof claimAmount === 'string' ? parseFloat(claimAmount) : claimAmount;
  
  if (amount <= 1500) {
    return 30;
  } else if (amount <= 5000) {
    return 50;
  } else if (amount <= 10000) {
    return 75;
  } else {
    return 100; // Claims over $10,000 for businesses/entities
  }
};

/**
 * Get claim type options for California civil claims
 * @returns {Array} List of claim types
 */
export const getClaimTypes = () => {
  return [
    'Breach of Contract',
    'Property Damage',
    'Personal Injury',
    'Unpaid Debt',
    'Unpaid Rent',
    'Return of Security Deposit',
    'Faulty Repair or Services',
    'Consumer Issue',
    'Other'
  ];
};